import * as React from 'react';
import numeral from 'numeral';
import {
  HeaderLabelContainer,
  YearCellIcon,
  ChangeCell,
  ChangeValue,
  TableEmptyValue,
  TrendIcon,
  HeaderLabelText,
} from './Table.styles';
import { ICurrencyFormat } from '../../utils/types';
import { getFormattedCurrency } from '../../utils/format';
import { ApIcon } from '@alixpartners/ui-components';

export const TableHeader = ({
  color,
  displayName,
  hideIcon,
  enableSorting,
  setSort,
  column,
}: {
  color: string;
  column: any;
  hideIcon?: boolean;
  displayName: string;
  setSort: (s: string, e: any) => void;
  enableSorting?: boolean;
}) => {
  const [ascSort, setAscSort] = React.useState('inactive');
  const [descSort, setDescSort] = React.useState('inactive');

  const onSortChanged = React.useCallback(() => {
    setAscSort(column.isSortAscending() ? 'active' : 'inactive');
    setDescSort(column.isSortDescending() ? 'active' : 'inactive');
  }, [column]);

  const onSortRequested = (order: any, event: any) => {
    setSort(order, event.shiftKey);
  };

  React.useEffect(() => {
    column.addEventListener('sortChanged', onSortChanged);
    onSortChanged();
  }, [onSortChanged, column]);

  const isAsc = ascSort === 'active';
  const isDesc = descSort === 'active';
  const isNoSort = !isAsc && !isDesc;
  return (
    <HeaderLabelContainer
      isRight={!hideIcon}
      onClick={(e) => {
        if (enableSorting) {
          onSortRequested(isDesc ? 'asc' : 'desc', e);
        }
      }}
    >
      {!hideIcon && <YearCellIcon color={color} />}
      <HeaderLabelText>{displayName}</HeaderLabelText>
      {enableSorting && !isNoSort ? (
        <ApIcon iconName={isAsc ? 'north' : 'south'} iconSize="12px" />
      ) : null}
    </HeaderLabelContainer>
  );
};

export const TableHeaderGroup = ({ displayName }: { displayName: string }) => (
  <HeaderLabelText>{displayName}</HeaderLabelText>
);

export const cellCurrencyRendererFramework = (
  params: {
    rowIndex: number;
    value: number;
  },
  format: ICurrencyFormat
) => {
  return cellRendererFramework(
    params.value,
    getFormattedCurrency({
      value: Math.abs(params.value),
      currencyFormat: format,
      isCurrencySign: true,
      isUnitSign: true,
      isIdealDenomination: true,
    })
  );
};

export const cellNumberRendererFramework = (
  params: {
    rowIndex: number;
    value: number;
  },
  format?: ICurrencyFormat
) => {
  return cellRendererFramework(
    params.value,
    numeral(Math.abs(params.value)).format('0,0')
  );
};

export const cellRendererFramework = (
  value?: number | string,
  formattedValue?: number | string
) => {
  return (
    <ChangeCell>
      {value === null || value === undefined ? (
        <TableEmptyValue>-</TableEmptyValue>
      ) : (
        <ChangeValue isSmallerFontSize={String(formattedValue).length > 10}>
          {formattedValue}
        </ChangeValue>
      )}
    </ChangeCell>
  );
};

export const varianceCurrencyCellRendererFramework = (
  params: {
    rowIndex: number;
    value: number;
  },
  format: ICurrencyFormat
) => {
  return varianceCellRendererFramework(
    params.value,
    getFormattedCurrency({
      value: Math.abs(params.value),
      currencyFormat: format,
      isCurrencySign: true,
      isUnitSign: true,
      isIdealDenomination: true,
    })
  );
};

export const varianceNumberCellRendererFramework = (
  params: {
    rowIndex: number;
    value: number;
  },
  format?: ICurrencyFormat
) => {
  return varianceCellRendererFramework(
    params.value,
    numeral(Math.abs(params.value)).format('0,0')
  );
};

export const varianceCellRendererFramework = (
  value?: number | string,
  formattedValue?: number | string
) => {
  return (
    <ChangeCell>
      {value === null || value === undefined ? (
        <TableEmptyValue>-</TableEmptyValue>
      ) : (
        <React.Fragment>
          {value !== 0 ? (
            <TrendIcon
              iconName={value > 0 ? 'arrow_upward' : 'arrow_downward'}
              iconSize={16}
              isNegative={value < 0}
            />
          ) : null}
          <ChangeValue isSmallerFontSize={String(formattedValue).length > 10}>
            {formattedValue}
          </ChangeValue>
        </React.Fragment>
      )}
    </ChangeCell>
  );
};
